import React, { useEffect, useState } from "react";

import Input from "../../controls/Input";
import {
  isValidUsername,
  normalizeUsername,
} from "../../../features/users/username";

import { useForm } from "../useForm";

const initialFValues = {
  name: "",
  email: "",
  username: "",
};

const primaryButtonClass =
  "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#2563EB] bg-[#2563EB] px-4 text-sm font-semibold text-white shadow-sm transition hover:border-[#1D4ED8] hover:bg-[#1D4ED8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB] disabled:opacity-50";

function SaveSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default function MyAccountForm(props) {
  const {
    updateAccountDetails,
    userDetails,
    loading = false,
    error = null,
  } = props;
  const [saved, setSaved] = useState(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if ("name" in fieldValues) {
      temp.name = null;
      if (fieldValues.name === "") {
        temp.name = "This field is required.";
      }
    }

    if ("email" in fieldValues) {
      temp.email = null;
      if (fieldValues.email === "") {
        temp.email = "This field is required.";
      }
    }

    if ("username" in fieldValues) {
      temp.username = isValidUsername(normalizeUsername(fieldValues.username))
        ? null
        : "Enter a username of up to 64 characters.";
    }

    setErrors({ ...temp });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    true,
    validate,
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setSaved(false);
      const success = await updateAccountDetails({
        ...values,
        username: normalizeUsername(values.username),
      });
      if (success) setSaved(true);
    }
  };

  useEffect(() => {
    setValues({
      name: userDetails.name ?? "",
      email: userDetails.email ?? "",
      username: userDetails.username ?? "",
    });
  }, [userDetails.name, userDetails.email, userDetails.username, setValues]);

  return (
    <section className="overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="border-b border-[#E2E8F0] px-6 py-5">
        <h2 className="text-base font-semibold text-[#0F172A]">
          Account details
        </h2>
        <p className="mt-1 text-sm text-[#64748B]">
          Update your name, email address, and username.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 p-6">
        <Input
          label="Name"
          name="name"
          onChange={handleInputChange}
          value={values.name}
          labelOn={true}
          type="text"
          error={errors.name || error?.errors?.name?.join(" ")}
        />

        <Input
          label="Email"
          name="email"
          onChange={handleInputChange}
          value={values.email}
          labelOn={true}
          type="email"
          error={errors.email || error?.errors?.email?.join(" ")}
        />

        <Input
          label="Username"
          name="username"
          onChange={handleInputChange}
          value={values.username}
          labelOn={true}
          type="text"
          error={errors.username || error?.errors?.username?.join(" ")}
        />

        {error?.title ? (
          <p role="alert" className="text-sm text-red-600">
            {error.title}
          </p>
        ) : null}
        {saved ? (
          <p role="status" className="text-sm text-green-700">
            Account details updated.
          </p>
        ) : null}

        <div className="flex justify-end border-t border-[#E2E8F0] pt-5">
          <button
            type="submit"
            className={primaryButtonClass}
            disabled={loading}
          >
            {loading ? <SaveSpinner /> : null}
            Save changes
          </button>
        </div>
      </form>
    </section>
  );
}
